import {
  Display_DisplayAlways,
  Display_ShowRequiredStatus,
  TextEditorDescription_TextInputType,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import type {
  InputDescription,
  ValueConfig_V,
  InputConfig,
  ListConfig,
  ValueConfig,
} from "@smartsuite/automation-engine-api";

import { translateIfPresent } from "../../../../../../../../../locales/i18n";
import type { InputCandidate } from "../../../../../../../../../types/candidate";

export const defaultDescription: InputDescription = {
  inputId: "",
  label: translateIfPresent("caption.value", "Value"),
  subLabel: "",
  value: {
    multiple: false,
    type: ValueDescription_T.STRING,
  },
  dynamic: false,
  display: {
    display: {
      oneofKind: "always",
      always: Display_DisplayAlways.ALWAYS,
    },
    showRequiredStatus: Display_ShowRequiredStatus.NOT_REQUIRED,
  },
  icon: "",
  editor: {
    editor: {
      oneofKind: "text",
      text: {
        default: "",
        type: TextEditorDescription_TextInputType.TYPE_SHORT,
        disablePills: true,
      },
    },
    validation: {
      oneofKind: undefined,
    },
    required: false,
  },
};

export const defaultInput: InputCandidate = {
  inputId: "",
  valueDescription: {
    multiple: false,
    type: ValueDescription_T.STRING,
  },
  value: {
    values: [],
  },
};

export const propertyKeyDescription: InputDescription = {
  inputId: "",
  label: translateIfPresent("caption.property-name", "Property Name"),
  subLabel: "",
  value: {
    multiple: false,
    type: ValueDescription_T.STRING,
  },
  dynamic: false,
  display: {
    display: {
      oneofKind: "always",
      always: Display_DisplayAlways.ALWAYS,
    },
    showRequiredStatus: Display_ShowRequiredStatus.NOT_REQUIRED,
  },
  icon: "",
  editor: {
    editor: {
      oneofKind: "text",
      text: {
        default: "",
        type: TextEditorDescription_TextInputType.TYPE_SHORT,
        disablePills: true,
      },
    },
    validation: {
      oneofKind: undefined,
    },
    required: true,
  },
};

export const defaultPropertyKeyInput: InputCandidate = {
  inputId: "",
  valueDescription: {
    multiple: false,
    type: ValueDescription_T.STRING,
  },
  value: {
    values: [],
  },
};

export const instructionDescription: InputDescription = {
  inputId: "",
  label: translateIfPresent("caption.instructions", "Instructions"),
  subLabel: "",
  value: {
    multiple: false,
    type: ValueDescription_T.STRING,
  },
  dynamic: false,
  display: {
    display: {
      oneofKind: "always",
      always: Display_DisplayAlways.ALWAYS,
    },
    showRequiredStatus: Display_ShowRequiredStatus.NOT_REQUIRED,
  },
  icon: "",
  editor: {
    editor: {
      oneofKind: "text",
      text: {
        default: "",
        type: TextEditorDescription_TextInputType.TYPE_LONG,
        disablePills: true,
        multiple: true,
      },
    },
    validation: {
      oneofKind: undefined,
    },
    required: false,
  },
};

export const emptyValueByType = (type: string): ValueConfig_V => {
  switch (type) {
    case "number":
      return {
        value: {
          oneofKind: "number",
          number: 0,
        },
      };
    case "boolean":
      return {
        value: {
          oneofKind: "bool",
          bool: false,
        },
      };
    case "json_object":
    case "keyValue":
      return {
        value: {
          oneofKind: "keyValue",
          keyValue: {
            key: {
              values: [],
            },
            value: {
              values: [],
            },
          },
        },
      };
    case "json_array":
    case "list":
      return {
        value: {
          oneofKind: "list",
          list: {
            values: [],
          },
        },
      };
    default:
      return {
        value: {
          oneofKind: "string",
          string: " ",
        },
      };
  }
};

export const getInputValueType = (
  input?: InputConfig,
  description?: InputDescription,
  valueDescription?: ValueDescription_T,
): string => {
  const inputValueType =
    input?.valueDescription?.type ?? ValueDescription_T.ANY;
  const descriptionType = description?.value?.type ?? ValueDescription_T.ANY;
  // add for backward compatibility, there were some cases when valueDescription was set wrong in existing automations
  const result = Math.max(inputValueType, descriptionType);
  switch (valueDescription ?? result) {
    case ValueDescription_T.KEY_VALUE:
      return "json_object";
    case ValueDescription_T.LIST: {
      if (input?.value?.values.some((v) => v.value.oneofKind === "reference")) {
        return "list";
      }
      return "json_array";
    }
    case ValueDescription_T.STRING:
      return "text";
    case ValueDescription_T.BOOL:
      return "boolean";
    case ValueDescription_T.NUMBER:
      return "number";
    default:
      return "";
  }
};

export const getListBodyType = (input: ListConfig): string => {
  if (
    input.values.length > 0 &&
    input.values[0].values.some((item) => item.value.oneofKind === "keyValue")
  ) {
    return "json_object";
  } else if (input.values.length > 0 && input.values[0].values.length > 0) {
    const kind = input.values[0].values[0].value.oneofKind;
    if (kind === "string") {
      return "text";
    } else if (kind === "number") {
      return "number";
    } else if (kind === "bool") {
      return "boolean";
    }
  }
  return "";
};
